import React, { useEffect, useState } from "react";
import {
  CreditCard,
  Plus,
  Download,
  Upload,
  History,
  Zap,
  Shield,
  Wallet,
  TrendingUp,
  Calendar,
  Settings,
  Eye,
  EyeOff,
} from "lucide-react";
import { CountResponse, Transaction, WalletBalanceData } from "../campaign/types";
import useRequest from "../../../components/hook/use-req";
import { formatCurrency } from "../../../utils/function";
import Pagination from "../../../components/pagination";
import AddFundsModal from "../../../components/marketing/wallet/funds-modal";
import { useNavigate } from "react-router-dom";

export default function WalletScreen() {
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();
  const [showBalance, setShowBalance] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [userEmail] = useState("user@example.com");
  const [walletBallance, setWlletBallance] = useState<WalletBalanceData | null>(null);
  const [count, setCount] = useState<CountResponse | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10); // Number of transactions per page
  const [totalPages, setTotalPages] = useState(1);
  const [totalTransactions, setTotalTransactions] = useState(0);

  const { makeRequest: WalletBalance } = useRequest("/wallet/balance", "GET", {
    Authorization: `Bearer ${token}`,
  });

  const [recentTransactions, setRecentTransactions] = useState<Transaction[]>([]);

  const { makeRequest: myTransaction } = useRequest("/wallet/transactions", "GET", {
    Authorization: `Bearer ${token}`,
  });

  const { makeRequest: getCampaignsCount } = useRequest(
    `/campaign/summary`,
    "GET",
    {
      Authorization: `Bearer ${token}`,
    }
  );

  useEffect(() => {
    const fetchData = async () => {
      const [response] = await WalletBalance();
      setWlletBallance(response?.data);
    };
    fetchData();
  }, []);

  // Fetch transactions with pagination
  useEffect(() => {
    const fetchTransactions = async () => {
      const [response] = await myTransaction({
        page: currentPage,
        limit: pageSize
      });

      if (response) {
        setRecentTransactions(response.transactions || []);
        setTotalPages(Math.ceil((response.total || 0) / pageSize));
        setTotalTransactions(response.total || 0);
      }
    };
    fetchTransactions();
  }, [currentPage, pageSize]); // Refetch when page changes

  useEffect(() => {
    const fetchData = async () => {
      const [response] = await getCampaignsCount();
      setCount(response?.response);
    };
    fetchData();
  }, []);

  // Small currency-symbol-only glyph next to "Current Balance" — was a
  // hardcoded ₦, which lied whenever the wallet's actual currency (from
  // GET /wallet/balance) wasn't NGN (e.g. INR).
  const currencySymbol = (() => {
    try {
      return (0)
        .toLocaleString("en-NG", {
          style: "currency",
          currency: walletBallance?.currency || "_",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
        .replace(/[0-9.,\s]/g, "");
    } catch {
      return "";
    }
  })();

  // Handle transaction click
  const handleTransactionClick = (transaction: Transaction) => {
    if (transaction.metadata?.campaign_id) {
      localStorage.setItem('draftCampaignId', transaction.metadata.campaign_id);
      navigate(`/campaign-report/${transaction.metadata.campaign_id}`);
    }
  };

  const getTotalMetrics = () => {
    if (!count?.data) return null;

    const { SMS, EMAIL, VOICE } = count.data;
    return {
      total_campaigns: (SMS?.total_campaigns || 0) + (EMAIL?.total_campaigns || 0) + (VOICE?.total_campaigns || 0),
      active_campaigns: (SMS?.active_campaigns || 0) + (EMAIL?.active_campaigns || 0) + (VOICE?.active_campaigns || 0),
      messages_sent: (SMS?.messages_sent || 0) + (EMAIL?.messages_sent || 0) + (VOICE?.messages_sent || 0),
      total_clicks: (SMS?.total_clicks || 0) + (EMAIL?.total_clicks || 0) + (VOICE?.total_clicks || 0),
      unique_clicks: (SMS?.unique_clicks || 0) + (EMAIL?.unique_clicks || 0) + (VOICE?.unique_clicks || 0)
    };
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Calculate displayed transactions range for showing "Showing X-Y of Z"
  const getDisplayedRange = () => {
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, totalTransactions);
    return { start, end };
  };

  const { start, end } = getDisplayedRange();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-600 rounded-lg">
              <Wallet className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Wallet</h1>
          </div>
          <p className="text-gray-600">
            Manage your account balance and transaction history
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Balance & Quick Actions */}
          <div className="lg:col-span-2 space-y-6">
            {/* Balance Card */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-700 rounded-2xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {currencySymbol}
                  <span className="text-blue-100">Current Balance</span>
                </div>
                <button
                  onClick={() => setShowBalance(!showBalance)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  {showBalance ? (
                    <Eye className="w-5 h-5" />
                  ) : (
                    <EyeOff className="w-5 h-5" />
                  )}
                </button>
              </div>
              <div className="mb-6">
                <div className="text-4xl font-bold mb-2">
                  {showBalance ? (
                    <span>
                      {formatCurrency(walletBallance?.balance || 0, walletBallance?.currency || 'NGN', 'symbol')}
                    </span>
                  ) : (
                    <span>
                      {'****'}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-blue-100">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-sm">+12.5% from last month</span>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setShowModal(true)}
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl p-4 transition-all hover:scale-105"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Plus className="w-5 h-5" />
                    </div>
                    <span className="font-medium">Add Funds</span>
                  </div>
                </button>
              </div>
            </div>

            {/* Usage Analytics */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  Usage Analytics
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-green-500 rounded-lg">
                      <Zap className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-green-700 font-medium">SMS Sent</span>
                  </div>
                  <div className="text-2xl font-bold text-green-800">
                    {count?.data?.SMS?.messages_sent}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-blue-500 rounded-lg">
                      <History className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-blue-700 font-medium">
                      Emails Sent
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-blue-800">{count?.data?.EMAIL?.messages_sent}</div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-purple-500 rounded-lg">
                      <Upload className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-purple-700 font-medium">
                      WhatsApp
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-purple-800">
                    0
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Transactions */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  Recent Transactions
                </h3>
                {totalTransactions > 0 && (
                  <div className="text-sm text-gray-500">
                    Showing {start}-{end} of {totalTransactions}
                  </div>
                )}
              </div>

              <div className="space-y-4">
                {recentTransactions.map((transaction) => (
                  <div
                    key={transaction.transaction_id}
                    onClick={() => handleTransactionClick(transaction)}
                    className={`flex items-center justify-between p-4 hover:bg-gray-50 rounded-xl transition-colors ${transaction.metadata?.campaign_id ? 'cursor-pointer hover:border hover:border-gray-200' : 'cursor-default'
                      }`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`p-3 rounded-lg ${transaction.type === "credit"
                          ? "bg-green-100 text-green-600"
                          : "bg-red-100 text-red-600"
                          }`}
                      >
                        {transaction.type === "credit" ? (
                          <Download className="w-5 h-5" />
                        ) : (
                          <Upload className="w-5 h-5" />
                        )}
                      </div>

                      <div>
                        <div className="font-medium text-gray-900">
                          {transaction.source || "Transaction"}
                          {transaction.metadata?.campaign_id && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                              Campaign
                            </span>
                          )}
                        </div>

                        <div className="text-sm text-gray-500 flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {new Date(transaction.created_at).toLocaleDateString("en-US")}
                        </div>
                      </div>
                    </div>

                    <div className="text-right">
                      <div
                        className={`font-semibold ${transaction.type === "credit"
                          ? "text-green-600"
                          : "text-red-600"
                          }`}
                      >
                        {formatCurrency(transaction?.amount || 0, `${transaction?.currency}`, 'symbol')}
                      </div>
                      <div className="text-sm text-gray-500 capitalize">
                        {transaction.type}
                      </div>
                    </div>
                  </div>
                ))}

                {recentTransactions.length === 0 && (
                  <p className="text-gray-500 text-center py-4">No transactions yet.</p>
                )}
              </div>

              {/* Pagination Component */}
              {totalPages > 1 && (
                <div className="mt-6">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    pageSize={pageSize}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Payment Methods & Settings */}
          <div className="space-y-6">
            {/* Payment Methods */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Payment Methods
                </h3>
                <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                  <Plus className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="border-2 border-blue-200 bg-blue-50 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <CreditCard className="w-6 h-6 text-blue-600" />
                    <span className="font-medium text-gray-900">•••• 4532</span>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                      Primary
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Visa • Expires 12/26
                  </div>
                </div>

                <div className="border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors cursor-pointer">
                  <div className="flex items-center gap-3 mb-3">
                    <CreditCard className="w-6 h-6 text-[#7B7B7B] font-semibold" />
                    <span className="font-medium text-gray-900">•••• 8901</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Mastercard • Expires 08/27
                  </div>
                </div>
              </div>
            </div>

            {/* Security & Limits */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="flex items-center gap-2 mb-6">
                <Shield className="w-6 h-6 text-gray-700" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Security & Limits
                </h3>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">
                    Daily Spending Limit
                  </span>
                  <span className="text-sm text-gray-900">$500.00</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">
                    Auto-reload
                  </span>
                  <span className="text-sm text-green-600">Enabled</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">
                    2FA Protection
                  </span>
                  <span className="text-sm text-green-600">Active</span>
                </div>
              </div>

              <button className="w-full mt-4 flex items-center justify-center gap-2 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                <Settings className="w-5 h-5" />
                Manage Settings
              </button>
            </div>

            {/* Quick Stats */}
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-6 text-white">
              <h3 className="text-lg font-semibold mb-4">This Month</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-indigo-100">Messages Sent</span>
                  <span className="font-semibold">24,410</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-indigo-100">Total Spent</span>
                  <span className="font-semibold">$60.25</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-indigo-100">Savings vs Plan</span>
                  <span className="font-semibold text-green-300">+$12.50</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <AddFundsModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        userEmail={userEmail}
      />
    </div>
  );
}